/**
 * Pixley Learn design tokens — grounded in the site's brand system
 * (Fredoka, brand blue, sticker shadows, 24–28px radii) instead of the
 * transcribed iOS palette. Every Learn surface styles through these.
 */
export const LEARN = {
  font: "'Fredoka', 'Inter', system-ui, sans-serif",
  color: {
    blue: "#0065F8",
    blueDeep: "#073B8E",
    blueSoft: "#EAF3FF",
    sky: "#BEE3FF",
    ink: "#0B1A33",
    muted: "#5A6B85",
    line: "rgba(11,26,51,0.08)",
    white: "#FFFFFF",
    sun: "#FFC221",
    sunDeep: "#C98F00",
    coral: "#FF5D73",
    coralDeep: "#B8384A",
    mint: "#3DDC84",
    mintDeep: "#1E8F52",
    lavender: "#946EF2",
    lavenderDeep: "#5B3FB3",
  },
  radius: { sm: 14, md: 20, lg: 26, xl: 32, pill: 999 },
  shadow: {
    card: "0px 4px 0px rgba(11,26,51,0.06), 0px 14px 30px rgba(11,26,51,0.08)",
    float: "0px 18px 40px rgba(11,26,51,0.14)",
    sticker: (deep = "#073B8E") => `0px 4px 0px ${deep}`,
    stickerPressed: (deep = "#073B8E") => `0px 1px 0px ${deep}`,
  },
  /** The floating site navbar: 24px top + 64px bar + 2px borders ≈ 92px. */
  navOffset: 104,
  navOffsetCompact: 96,
  /** Per-course accents. */
  course: {
    letters: { accent: "#3DDC84", deep: "#1E8F52", wash: "#E3F6EE", label: "Letters" },
    counting: { accent: "#FFC221", deep: "#C98F00", wash: "#FFF6DE", label: "Counting" },
    writing: { accent: "#946EF2", deep: "#5B3FB3", wash: "#EFE9FF", label: "Writing" },
  },
  /** Alphabet world accents (map + game chrome). */
  world: {
    garden: { accent: "#2EA64F", deep: "#1A6B32" },
    ocean: { accent: "#0A8AD1", deep: "#065A8A" },
    sky: { accent: "#2E7AE0", deep: "#1B4E96" },
    star: { accent: "#754DC9", deep: "#4A2E85" },
    rainbow: { accent: "#E34070", deep: "#9A2649" },
  },
  motion: {
    spring: "cubic-bezier(0.22, 1, 0.36, 1)",
    pop: "cubic-bezier(0.34, 1.56, 0.64, 1)",
    fast: 160,
    base: 260,
    slow: 480,
  },
  bp: { sm: 640, md: 900, lg: 1200 },
};

/** Chunky Pixley "sticker" button style. */
export function stickerButton({ bg = LEARN.color.blue, deep = LEARN.color.blueDeep, color = "#fff", size = "md" } = {}) {
  const pad = size === "lg" ? "16px 34px" : size === "sm" ? "9px 18px" : "13px 26px";
  const font = size === "lg" ? 19 : size === "sm" ? 14 : 16;
  return {
    fontFamily: LEARN.font,
    fontWeight: 700,
    fontSize: font,
    color,
    background: bg,
    border: "none",
    borderRadius: LEARN.radius.pill,
    padding: pad,
    boxShadow: LEARN.shadow.sticker(deep),
    cursor: "pointer",
    userSelect: "none",
    WebkitTapHighlightColor: "transparent",
    transition: `transform ${LEARN.motion.fast}ms ${LEARN.motion.spring}, box-shadow ${LEARN.motion.fast}ms`,
  };
}

export function usePrefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
